// ideaLearningStudioEditor/_visor/utils/recorderMode.js
//
// Recorder protocol bridge between VisorApp and the headless recorder
// (idea-middleware2 RecorderBrowserService). Activated only when the URL
// contains `?recorderMode=true`. All side-effects are window/DOM scoped;
// no React state.
//
// Cross-repo contract — every string constant + dispatched event name
// below is read or written by RecorderBrowserService. Activation also
// requires that the middleware (a) appends `?recorderMode=true` to the
// scene URL and (b) no longer injects its own `__rec_overlay__` div.
// Both are handled in companion middleware tasks; until they land, the
// helpers below are inert (isRecorderMode() returns false).

const OVERLAY_ID = "__rec_overlay__";
const STATUS_KEY = "__SCENE_RECORDER_STATUS";
const TRIGGER_KEY = "__SCENE_RECORDER_TRIGGER_PLAY";

export function isRecorderMode() {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("recorderMode") === "true";
  } catch (e) {
    return false;
  }
}

/**
 * renderMode is a layout flag (separate from recorderMode's protocol).
 * When ?renderMode=true is in the URL the visor strips the public-preview
 * header bar, removes wrapper padding, and locks the document to a fixed
 * 1920x1080 surface so the captured tab matches the scene's authoring
 * raster exactly. recorderMode and renderMode can coexist on the same URL.
 */
export function isRenderMode() {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("renderMode") === "true";
  } catch (e) {
    return false;
  }
}

export function showOverlay() {
  if (typeof document === "undefined") return;
  if (document.getElementById(OVERLAY_ID)) return;
  const el = document.createElement("div");
  el.id = OVERLAY_ID;
  el.style.cssText =
    "position:fixed;top:0;left:0;width:100vw;height:100vh;" +
    "background:#000;z-index:2147483647;pointer-events:none;";
  document.body.appendChild(el);
}

export function hideOverlay() {
  if (typeof document === "undefined") return;
  const el = document.getElementById(OVERLAY_ID);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

export function setStatus(status) {
  if (typeof window === "undefined") return;
  window[STATUS_KEY] = status;
  try {
    window.dispatchEvent(new CustomEvent(`scene-recorder-${status}`));
  } catch (e) {
    // CustomEvent may be polyfilled in older runtimes; a missing event
    // is non-fatal — the puppeteer-side status poll picks up window[STATUS_KEY].
  }
}

export function exposeTrigger(fn) {
  if (typeof window === "undefined") return;
  window[TRIGGER_KEY] = fn;
}

export function clearTrigger() {
  if (typeof window === "undefined") return;
  try {
    delete window[TRIGGER_KEY];
  } catch (e) {
    window[TRIGGER_KEY] = undefined;
  }
}

/**
 * Publish the per-scene authoritative timing manifest computed by the
 * visor at scene-mount. The recorder reads window.__SCENE_RECORDER_TIMING
 * after setStatus('ready') fires.
 *
 * @typedef {Object} SceneTimingBounds
 * @property {number} lastAudioEndSec
 * @property {number} lastVideoEndSec
 * @property {number} lastAnimEndSec
 * @property {number} lastBoxEndSec
 *
 * @typedef {Object} SceneTimingElement
 * @property {string} id
 * @property {string} type
 * @property {number} startSec
 * @property {number} endSec
 * @property {number} [trimStartSec]
 * @property {number} [trimEndSec]
 *
 * @typedef {Object} SceneTimingLifecycleEvent
 * @property {number} tSec   playhead seconds (0 = trigger instant)
 * @property {string} event
 * @property {string} [id]
 *
 * @typedef {Object} SceneTiming
 * @property {number} sceneDurationSec
 * @property {number} trueEndSec
 * @property {number} authorTailBufferSec
 * @property {SceneTimingBounds} bounds
 * @property {SceneTimingElement[]} elements
 * @property {SceneTimingLifecycleEvent[]} lifecycle
 */

const TIMING_KEY = "__SCENE_RECORDER_TIMING";

export function setSceneTiming(timing) {
  if (typeof window === "undefined") return;
  window[TIMING_KEY] = timing;
}

export function getSceneTiming() {
  if (typeof window === "undefined") return null;
  return window[TIMING_KEY] || null;
}

export function appendLifecycleEvent(event, tSec, id) {
  if (typeof window === "undefined") return;
  const t = window[TIMING_KEY];
  if (!t || !Array.isArray(t.lifecycle)) return;
  t.lifecycle.push(id ? { tSec, event, id } : { tSec, event });
}

// ─────────────────────────────────────────────────────────────────────────────
// Browser self-record helpers (additive — does NOT replace the x11grab path)
//
// Captures the current tab via getDisplayMedia({preferCurrentTab:true}) and
// records to a WebM blob with MediaRecorder. The blob is downloaded as a
// file via an <a download> click — Puppeteer can catch this through
// Browser.setDownloadBehavior + downloadProgress events.
//
// Tab capture audio (`audio: true`) captures Web Audio API destination
// (Howler), <video>, and <audio> in one stream — same source clock as
// the video track, so A/V sync is automatic.
//
// Requires headed Chromium with --auto-accept-this-tab-capture launch flag
// (and optionally --auto-select-tab-capture-source-by-title=<title>).
//
// These functions are exposed on window when the page is in recorderMode,
// so Puppeteer can call them via page.evaluate.
// ─────────────────────────────────────────────────────────────────────────────

let _mediaRecorder = null;
let _recordedChunks = [];
let _recordingStream = null;

export async function startBrowserRecord(opts = {}) {
  if (typeof navigator === "undefined" || !navigator.mediaDevices) {
    throw new Error("navigator.mediaDevices unavailable in this context");
  }
  if (_mediaRecorder) {
    throw new Error("Browser record already active — call stopBrowserRecord first");
  }

  const fps = opts.fps || 30;
  const videoBps = opts.videoBitsPerSecond || 8_000_000;
  const audioBps = opts.audioBitsPerSecond || 192_000;

  // getDisplayMedia for tab self-capture. preferCurrentTab tells Chromium
  // to skip the surface picker; --auto-accept-this-tab-capture launch flag
  // bypasses the share-this-tab confirmation dialog.
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: {
      frameRate: { ideal: fps, max: fps },
      displaySurface: "browser",
    },
    audio: true,
    preferCurrentTab: true,
    selfBrowserSurface: "include",
    surfaceSwitching: "exclude",
  });

  // Sanity: confirm both tracks are present.
  const videoTracks = stream.getVideoTracks();
  const audioTracks = stream.getAudioTracks();
  if (videoTracks.length === 0) {
    stream.getTracks().forEach((t) => t.stop());
    throw new Error("getDisplayMedia returned no video track");
  }

  _recordingStream = stream;
  _recordedChunks = [];

  // Surface a clear signal when the OS / Chrome itself ends a track —
  // tab capture can be killed by the "Stop sharing" infobar, by the
  // user closing the source tab, or (rarely) by Chromium internals.
  // Without this hook those failures look identical to a renderer
  // crash from the host's perspective, so the test harness has no
  // way to distinguish "MediaRecorder voluntarily ended" from
  // "page died." Log every track endpoint with a tag the host
  // already forwards via console listener.
  try {
    videoTracks.forEach((t, i) => {
      t.onended = () => {
        // eslint-disable-next-line no-console
        console.error(
          `[browser-record] video track ${i} ended unexpectedly (readyState=${t.readyState})`
        );
      };
      t.onmute = () => {
        // eslint-disable-next-line no-console
        console.error(`[browser-record] video track ${i} muted`);
      };
    });
    audioTracks.forEach((t, i) => {
      t.onended = () => {
        // eslint-disable-next-line no-console
        console.error(`[browser-record] audio track ${i} ended unexpectedly`);
      };
    });
  } catch (e) {
    /* track event hooks are diagnostic-only; never fail recording over them */
  }

  // VP9/Opus first; fall back to VP8/Opus if VP9 isn't supported on this build.
  // (Chrome for Testing builds have ALL VPx codecs but no H.264 — never use mp4.)
  let mimeType = "video/webm;codecs=vp9,opus";
  if (typeof MediaRecorder !== "undefined" && !MediaRecorder.isTypeSupported(mimeType)) {
    mimeType = "video/webm;codecs=vp8,opus";
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = "video/webm";
    }
  }

  _mediaRecorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: videoBps,
    audioBitsPerSecond: audioBps,
  });

  _mediaRecorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) _recordedChunks.push(e.data);
  };
  _mediaRecorder.onerror = (e) => {
    // eslint-disable-next-line no-console
    console.error("[browser-record] MediaRecorder error:", e.error || e);
  };

  // Emit data every 250ms so partial recordings survive an unexpected stop.
  _mediaRecorder.start(250);

  return {
    mimeType,
    videoTracks: videoTracks.length,
    audioTracks: audioTracks.length,
    state: _mediaRecorder.state,
  };
}

// Three-phase stop: stop → getBlob → cleanup. Splitting these makes each
// page.evaluate round-trip short and avoids the Chromium bug where
// stopping the capture stream's tracks during an active CDP call can
// invalidate the execution context (the trigger for puppeteer#13478 +
// related TargetCloseError observations). Tracks are stopped only in the
// dedicated cleanup phase, after the host has fully retrieved the blob.

let _lastBlob = null;
let _lastBlobSize = 0;

export function stopBrowserRecord() {
  return new Promise((resolve, reject) => {
    if (!_mediaRecorder) {
      return reject(new Error("No active browser record"));
    }
    if (_mediaRecorder.state === "inactive") {
      return reject(new Error("Browser recorder already inactive"));
    }
    _mediaRecorder.onstop = () => {
      try {
        const blob = new Blob(_recordedChunks, { type: "video/webm" });
        _lastBlob = blob;
        _lastBlobSize = blob.size;
        resolve({ size: blob.size });
      } catch (err) {
        reject(err);
      }
    };
    try {
      _mediaRecorder.stop();
    } catch (err) {
      reject(err);
    }
  });
}

export function getBrowserRecordBlobAsDataUrl() {
  return new Promise((resolve, reject) => {
    if (!_lastBlob) return reject(new Error("No blob available — call stopBrowserRecord first"));
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
    reader.readAsDataURL(_lastBlob);
  });
}

/**
 * Read the recorded blob as a chunk of base64 from byteOffset,
 * `chunkSize` bytes long. Lets the host pull large recordings in
 * pieces over CDP without slamming a multi-MB string through one
 * page.evaluate call. Returns { eof: bool, base64: string }.
 */
export async function getBrowserRecordBlobChunk(byteOffset, chunkSize) {
  if (!_lastBlob) throw new Error("No blob available");
  const start = Math.max(0, byteOffset | 0);
  const end = Math.min(_lastBlob.size, start + (chunkSize | 0));
  if (start >= _lastBlob.size) return { eof: true, base64: "" };
  const slice = _lastBlob.slice(start, end);
  const buf = await slice.arrayBuffer();
  // Base64-encode the chunk bytes
  let binary = "";
  const bytes = new Uint8Array(buf);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
  const base64 = typeof btoa === "function" ? btoa(binary) : "";
  return { eof: end >= _lastBlob.size, base64, bytesRead: end - start };
}

export function cleanupBrowserRecord() {
  if (_recordingStream) {
    _recordingStream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch (e) {
        /* noop */
      }
    });
  }
  _recordingStream = null;
  _mediaRecorder = null;
  _recordedChunks = [];
  _lastBlob = null;
  _lastBlobSize = 0;
  return true;
}

export function isBrowserRecordActive() {
  return _mediaRecorder != null && _mediaRecorder.state === "recording";
}

export function getBrowserRecordSize() {
  return _lastBlobSize;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hyperframes-style determinism protocol (additive)
//
// Window-key contract for the host-driven seek model. The page never plays
// audio in recorder mode — the audio manifest is consumed offline by FFmpeg.
// Existing real-time MediaRecorder helpers above still ship for backwards
// compatibility, but the new contract supersedes them when the middleware
// uses the seekFrame loop.
// ─────────────────────────────────────────────────────────────────────────────

const READY_KEY = "__SCENE_RECORDER_READY";
const DURATION_KEY = "__SCENE_RECORDER_DURATION_FRAMES";
const SEEK_KEY = "__SCENE_RECORDER_SEEK_FRAME";
const MANIFEST_KEY = "__SCENE_RECORDER_AUDIO_MANIFEST";

export function setReady(ready) {
  if (typeof window === "undefined") return;
  window[READY_KEY] = !!ready;
  try {
    window.dispatchEvent(new CustomEvent(`scene-recorder-ready-${ready ? "true" : "false"}`));
  } catch (e) {
    // CustomEvent polyfilling — non-fatal, the host polls the key directly.
  }
}

export function isReady() {
  if (typeof window === "undefined") return false;
  return window[READY_KEY] === true;
}

export function setDurationFrames(frames) {
  if (typeof window === "undefined") return;
  window[DURATION_KEY] = Number(frames) | 0;
}

export function installSeekFn(fn) {
  if (typeof window === "undefined") return;
  window[SEEK_KEY] = fn;
}

export function clearSeekFn() {
  if (typeof window === "undefined") return;
  try {
    delete window[SEEK_KEY];
  } catch (e) {
    window[SEEK_KEY] = undefined;
  }
}

export function setAudioManifest(manifest) {
  if (typeof window === "undefined") return;
  window[MANIFEST_KEY] = manifest;
}

export function getAudioManifest() {
  if (typeof window === "undefined") return null;
  return window[MANIFEST_KEY] || null;
}

// === Task 17 recorder skip-interactive ===
/**
 * Returns true when the given scene contains an InteractivePlugin box with items[].length > 0.
 * Used by:
 * - VisorApp recorder branch to skip the scene
 * - Workflow event flow gates to suppress emits when recorder is active
 *
 * @param {{ id: string, boxes?: string[] } | null | undefined} navItem
 * @param {Object<string, any> | null | undefined} _boxesById (unused but kept for symmetry)
 * @param {Object<string, { pluginId?: string, state?: { items?: any[] } }> | null | undefined} pluginToolbarsById
 * @returns {boolean}
 */
export function isInteractiveScene(navItem, _boxesById, pluginToolbarsById) {
  if (!navItem || !navItem.boxes) return false;
  for (const boxId of navItem.boxes) {
    const tb = pluginToolbarsById && pluginToolbarsById[boxId];
    if (!tb) continue;
    if (tb.pluginId === "InteractivePlugin") {
      const items = tb.state && tb.state.items;
      if (Array.isArray(items) && items.length > 0) return true;
    }
  }
  return false;
}
// === End Task 17 recorder skip-interactive ===
